import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/* MomoLulu free shipping progress bar in cart drawer */
(function () {
  if (window.momoluluFreeShippingDrawerLoaded) return;
  window.momoluluFreeShippingDrawerLoaded = true;

  var FREE_SHIPPING_THRESHOLD = 4900; // $49.00

  function addStyle() {
    if (document.getElementById('momolulu-free-shipping-drawer-style')) return;

    var style = document.createElement('style');
    style.id = 'momolulu-free-shipping-drawer-style';
    style.textContent = `
      .momolulu-drawer-free-shipping {
        margin: 16px 0 18px;
        padding: 14px 16px;
        background: #f4f8f2;
        border: 1px solid #d8e5d6;
        border-radius: 12px;
        text-align: center;
      }

      .momolulu-drawer-free-shipping__text {
        font-size: 14px;
        font-weight: 600;
        color: #2f2f2f;
        margin-bottom: 10px;
        line-height: 1.4;
      }

      .momolulu-drawer-free-shipping__track {
        width: 100%;
        height: 8px;
        background: #e4ece1;
        border-radius: 999px;
        overflow: hidden;
      }

      .momolulu-drawer-free-shipping__fill {
        height: 100%;
        width: 0%;
        background: #8fae8b;
        border-radius: 999px;
        transition: width 0.3s ease;
      }

      .momolulu-drawer-free-shipping__note {
        font-size: 12px;
        color: #555;
        margin-top: 8px;
      }

      .momolulu-drawer-free-shipping.is-unlocked {
        background: #eef6ec;
        border-color: #b9d0b5;
      }
    `;

    document.head.appendChild(style);
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  function findCheckoutButton() {
    var selectors = [
      'button[name="checkout"]',
      'input[name="checkout"]',
      'a[href="/checkout"]',
      'a[href*="/checkout"]'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var element = document.querySelector(selectors[i]);
      if (element) return element;
    }

    return null;
  }

  function ensureBar() {
    addStyle();

    var existingBar = document.querySelector('[data-momolulu-free-shipping-drawer]');
    if (existingBar) return existingBar;

    var checkoutButton = findCheckoutButton();
    if (!checkoutButton || !checkoutButton.parentNode) return null;

    var bar = document.createElement('div');
    bar.className = 'momolulu-drawer-free-shipping';
    bar.setAttribute('data-momolulu-free-shipping-drawer', 'true');

    bar.innerHTML = `
      <div class="momolulu-drawer-free-shipping__text" data-momolulu-free-shipping-text>
        You’re $49.00 away from free U.S. shipping.
      </div>

      <div class="momolulu-drawer-free-shipping__track">
        <div class="momolulu-drawer-free-shipping__fill" data-momolulu-free-shipping-fill></div>
      </div>

      <div class="momolulu-drawer-free-shipping__note">
        Free U.S. shipping on orders over $49.
      </div>
    `;

    checkoutButton.parentNode.insertBefore(bar, checkoutButton);

    return bar;
  }

  function updateBar() {
    var bar = ensureBar();
    if (!bar) return;

    var text = bar.querySelector('[data-momolulu-free-shipping-text]');
    var fill = bar.querySelector('[data-momolulu-free-shipping-fill]');

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var total = cart.total_price || 0;
        var remaining = Math.max(FREE_SHIPPING_THRESHOLD - total, 0);
        var progress = Math.min((total / FREE_SHIPPING_THRESHOLD) * 100, 100);

        if (total >= FREE_SHIPPING_THRESHOLD) {
          text.innerHTML = '🎉 You’ve unlocked free U.S. shipping!';
          bar.classList.add('is-unlocked');
        } else {
          text.innerHTML = 'You’re <strong>' + formatMoney(remaining) + '</strong> away from free U.S. shipping.';
          bar.classList.remove('is-unlocked');
        }

        fill.style.width = progress + '%';
      })
      .catch(function () {});
  }

  function delayedUpdate() {
    setTimeout(updateBar, 400);
    setTimeout(updateBar, 1000);
  }

  document.addEventListener('click', delayedUpdate);
  document.addEventListener('change', delayedUpdate);
  document.addEventListener('cart:updated', delayedUpdate);
  document.addEventListener('cart:refresh', delayedUpdate);

  var observer = new MutationObserver(function () {
    delayedUpdate();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  delayedUpdate();
})();